// Date utility helpers for Jalali (Shamsi), Hijri (Ghamari), Gregorian, and Swedish/Iranian Events

import { toPersianDigits } from './storage';

export interface DateDetails {
  weekdayName: string;         // e.g. "چهارشنبه" or "Wednesday" or "onsdag"
  gregorianDateOnly: string;   // e.g. "September 2, 2026" or "۲ سپتامبر ۲۰۲۶"
  jalaliDateOnly: string;      // e.g. "۱۱ شهریور ۱۴۰۵"
  hijriDateOnly: string;       // e.g. "۱۰ ربیع‌الاول ۱۴۴۸"
  
  jalaliFormatted: string; // e.g. "شنبه، ۱۸ مرداد ۱۴۰۵"
  jalaliDayName: string;   // e.g. "شنبه"
  jalaliDayNum: string;    // e.g. "۱۸"
  jalaliMonthName: string; // e.g. "مرداد"
  jalaliYear: string;      // e.g. "۱۴۰۵"
  
  hijriFormatted: string;  // e.g. "۲۴ صفر ۱۴۴۸"
  gregorianFormatted: string; // e.g. "۸ اوت ۲۰۲۶ (8 August 2026)"
  
  iranEvents: string[];    // Events in Iran for this date
  swedenEvents: string[];  // Events / Name days / Flag days in Sweden for this date
}

// Persian week days mapping
const PERSIAN_WEEKDAYS = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه'];

// Hijri months in Persian
const HIJRI_MONTHS = [
  'محرم', 'صفر', 'ربیع‌الاول', 'ربیع‌الثانی', 
  'جمادی‌الاول', 'جمادی‌الثانی', 'رجب', 'شعبان', 
  'رمضان', 'شوال', 'ذیقعده', 'ذیحجه'
];

// Gregorian months in Persian
const GREGORIAN_MONTHS_FA = [
  'ژانویه', 'فوریه', 'مارس', 'آوریل', 'می', 'ژوئن',
  'ژوئیه', 'اوت', 'سپتامبر', 'اکتبر', 'نوامبر', 'دسامبر'
];

const GREGORIAN_MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Notable Swedish Events & Flag Days (by Month-Day e.g., "8-8" for August 8)
const SWEDEN_EVENTS: Record<string, string[]> = {
  "1-1": ["Nyårsdagen - روز سال نو میلادی (روز تعطیل رسمی سوئد 🇸🇪)"],
  "1-6": ["Trettondedag jul - روز ۱۳ کریسمس (تعطیل رسمی در سوئد 🇸🇪)"],
  "1-28": ["Konungens namnsdag - روز نام‌گذاری پادشاه کارل شانزدهم گوستاف (روز پرچم رسمی 🇸🇪)"],
  "3-12": ["Kronprinsessan Victorias namnsdag - روز نام‌گذاری شاهزاده ویکتوریا (روز پرچم رسمی 🇸🇪)"],
  "4-30": ["Konungens födelsedag - زادروز پادشاه کارل شانزدهم گوستاف & Valborg (روز پرچم 🇸🇪)"],
  "5-1": ["Förستا maj - روز جهانی کارگر (روز پرچم و تعطیل رسمی در سوئد 🇸🇪)"],
  "6-6": ["Sveriges nationaldag - روز ملی و روز پرچم سوئد (روز تعطیل رسمی 🇸🇪)"],
  "6-20": ["Midsommarafton - جشن میدسامر در سوئد (از مهم‌ترین جشن‌های ملی سوئد 🇸🇪)"],
  "6-21": ["Midsommardagen - روز میدسامر (روز پرچم و تعطیل رسمی سوئد 🇸🇪)"],
  "7-14": ["Kronprinsessan Victorias födelsedag - زادروز شاهزاده ویکتوریا (روز پرچم رسمی 🇸🇪)"],
  "8-8": ["Drottning Silvias namnsdag - روز نام‌گذاری ملکه سیلویا (روز پرچم رسمی در سراسر سوئد 🇸🇪)"],
  "10-24": ["FN-dagen - روز سازمان ملل متحد (روز پرچم در سوئد 🇸🇪)"],
  "11-6": ["Gustav Adolfsdagen - روز گوستاو آدولف (روز پرچم در سوئد 🇸🇪)"],
  "12-10": ["Nobeldagen - روز اهدای جوایز نوبل در استکهلم (روز پرچم رسمی 🇸🇪)"],
  "12-13": ["Luciamorgon - جشن سنتی لوسیا و روشنایی در سوئد 🇸🇪"],
  "12-23": ["Drottningens födelsedag - زادروز ملکه سیلویا (روز پرچم رسمی 🇸🇪)"],
  "12-24": ["Julafton - شب کریسمس (مهم‌ترین جشن زمستانی خانواده‌ها در سوئد 🇸🇪)"],
  "12-25": ["Juldagen - روز کریسمس (تعطیل رسمی در سوئد 🇸🇪)"],
  "12-26": ["Annnandag jul - روز دوم کریسمس (تعطیل رسمی در سوئد 🇸🇪)"],
  "12-31": ["Nyårsafton - شب سال نو میلادی 🇸🇪"]
};

// Notable Iranian Events (by Shamsi Month-Day e.g. "5-18" for 18 Mordad)
const IRAN_EVENTS: Record<string, string[]> = {
  "1-1": ["عید نوروز - آغاز سال نو خورشیدی (تعطیل رسمی) 🇮🇷"],
  "1-2": ["عید نوروز (تعطیل رسمی) 🇮🇷"],
  "1-3": ["عید نوروز (تعطیل رسمی) 🇮🇷"],
  "1-4": ["عید نوروز (تعطیل رسمی) 🇮🇷"],
  "1-12": ["روز جمهوری اسلامی ایران (تعطیل رسمی) 🇮🇷"],
  "1-13": ["روز طبیعت - سیزده‌بدر (تعطیل رسمی) 🇮🇷"],
  "1-29": ["روز ارتش جمهوری اسلامی ایران 🇮🇷"],
  "2-1": ["روز بزرگداشت سعدی شیرازی 🇮🇷"],
  "2-12": ["روز معلم و شهادت استاد مرتضی مطهری 🇮🇷"],
  "2-25": ["روز پاسداشت زبان فارسی و بزرگداشت حکیم فردوسی 🇮🇷"],
  "2-28": ["روز بزرگداشت حکیم عمر خیام 🇮🇷"],
  "3-3": ["روز فتح خرمشهر و روز مقاومت، ایثار و پیروزی 🇮🇷"],
  "3-14": ["رحلت امام خمینی (تعطیل رسمی) 🇮🇷"],
  "3-15": ["قیام خونین ۱۵ خرداد (تعطیل رسمی) 🇮🇷"],
  "4-7": ["شهادت آیت‌الله بهشتی و ۷۲ تن از یاران و روز قوه قضائیه 🇮🇷"],
  "4-8": ["روز مبارزه با سلاح‌های شیمیایی و میکروبی 🇮🇷"],
  "5-8": ["روز بزرگداشت شیخ شهاب‌الدین سهروردی (شیخ اشراق) 🇮🇷"],
  "5-14": ["روز صدور فرمان مشروطیت 🇮🇷"],
  "5-17": ["روز خبرنگار (سالروز شهادت محمود صارمی) 🇮🇷"],
  "5-18": ["روز بزرگداشت شهدای مدافع حرم 🇮🇷"],
  "5-26": ["سالروز بازگشت آزادگان سرافراز به میهن اسلامی 🇮🇷"],
  "6-1": ["روز پزشک - بزرگداشت ابوعلی سینا 🇮🇷"],
  "6-2": ["آغاز هفته دولت 🇮🇷"],
  "6-4": ["روز کارمند 🇮🇷"],
  "6-5": ["روز داروساز - بزرگداشت محمد بن زکریای رازی 🇮🇷"],
  "6-13": ["روز بزرگداشت ابوریحان بیرونی 🇮🇷"],
  "6-27": ["روز شعر و ادب فارسی - بزرگداشت استاد شهریار 🇮🇷"],
  "7-8": ["روز بزرگداشت مولوی 🇮🇷"],
  "7-20": ["روز بزرگداشت حافظ شیرازی 🇮🇷"],
  "8-13": ["روز دانش‌آموز و روز ملی مبارزه با استکبار 🇮🇷"],
  "9-16": ["روز دانشجو 🇮🇷"],
  "9-30": ["شب یلدا - بلندترین شب سال و جشن کهن ایرانی 🇮🇷"],
  "11-22": ["پیروزی انقلاب اسلامی ایران (تعطیل رسمی) 🇮🇷"],
  "12-29": ["روز ملی شدن صنعت نفت ایران (تعطیل رسمی) 🇮🇷"]
};

export function getDateDetails(dateStr: string, lang: 'fa' | 'sv' | 'en' | string = 'fa'): DateDetails {
  const d = parseLocalDate(dateStr);
  const locale = lang === 'sv' ? 'sv-SE' : lang === 'en' ? 'en-US' : 'fa-IR';
  
  // Single Weekday Name
  let weekdayName = '';
  try {
    weekdayName = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(d);
  } catch (e) {
    weekdayName = PERSIAN_WEEKDAYS[d.getDay()];
  }

  // 1. Gregorian Date Only
  let gregorianDateOnly = '';
  try {
    gregorianDateOnly = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric', calendar: 'gregory' }).format(d);
  } catch (e) {
    gregorianDateOnly = `${d.getDate()} ${GREGORIAN_MONTHS_EN[d.getMonth()]} ${d.getFullYear()}`;
  }

  // 2. Jalali (Shamsi) Date Formatting
  const weekdayFa = PERSIAN_WEEKDAYS[d.getDay()];
  let jalaliDayNumStr = '';
  let jalaliMonthNameStr = '';
  let jalaliYearStr = '';
  let jalaliMonthNum = 0;
  let jalaliDayNum = 0;

  try {
    const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).formatToParts(d);

    for (const part of parts) {
      if (part.type === 'day') jalaliDayNumStr = part.value;
      if (part.type === 'month') jalaliMonthNameStr = part.value;
      if (part.type === 'year') jalaliYearStr = part.value;
    }

    const numParts = new Intl.DateTimeFormat('en-US-u-ca-persian', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    }).formatToParts(d);

    for (const part of numParts) {
      if (part.type === 'month') jalaliMonthNum = parseInt(part.value, 10);
      if (part.type === 'day') jalaliDayNum = parseInt(part.value, 10);
    }
  } catch (e) {
    jalaliDayNumStr = toPersianDigits(d.getDate());
    jalaliMonthNameStr = 'مرداد';
    jalaliYearStr = toPersianDigits(1405);
  }

  const jalaliDateOnly = `${jalaliDayNumStr} ${jalaliMonthNameStr} ${jalaliYearStr}`;
  const jalaliFormatted = `${weekdayFa}، ${jalaliDateOnly}`;

  // 3. Hijri (Ghamari) Date Formatting
  let hijriDateOnly = '';
  try {
    const formatter = new Intl.DateTimeFormat('fa-IR-u-ca-islamic-tbla', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    });
    const parts = formatter.formatToParts(d);
    let day = '', month = '', year = '';
    for (const p of parts) {
      if (p.type === 'day') day = p.value;
      if (p.type === 'month') month = p.value;
      if (p.type === 'year') year = p.value;
    }

    const HIJRI_MONTH_NAMES = [
      'محرم', 'صفر', 'ربیع‌الاول', 'ربیع‌الثانی',
      'جمادی‌الاول', 'جمادی‌الثانی', 'رجب', 'شعبان',
      'رمضان', 'شوال', 'ذیقعده', 'ذیحجه'
    ];

    const monthIdx = Math.max(0, (parseInt(month, 10) - 1) % 12);
    const monthName = HIJRI_MONTH_NAMES[monthIdx] || 'صفر';

    const faDay = toPersianDigits(day);
    const faYear = toPersianDigits(year);

    hijriDateOnly = `${faDay} ${monthName} ${faYear}`;
  } catch (e) {
    hijriDateOnly = '۲۴ صفر ۱۴۴۸';
  }

  const hijriFormatted = hijriDateOnly;
  const gregorianFormatted = `${d.getDate()} ${GREGORIAN_MONTHS_EN[d.getMonth()]} ${d.getFullYear()}`;

  // 4. Events Lookup
  const gMonthDayKey = `${d.getMonth() + 1}-${d.getDate()}`;
  const swedenEvents = SWEDEN_EVENTS[gMonthDayKey] || [];

  const jMonthDayKey = `${jalaliMonthNum}-${jalaliDayNum}`;
  const iranEvents = IRAN_EVENTS[jMonthDayKey] || [];

  return {
    weekdayName,
    gregorianDateOnly,
    jalaliDateOnly,
    hijriDateOnly,
    jalaliFormatted,
    jalaliDayName: weekdayFa,
    jalaliDayNum: jalaliDayNumStr,
    jalaliMonthName: jalaliMonthNameStr,
    jalaliYear: jalaliYearStr,
    hijriFormatted,
    gregorianFormatted,
    iranEvents,
    swedenEvents
  };
}

/**
 * Safely parses a YYYY-MM-DD or ISO date string into a Date object without timezone offset shifts.
 */
export function parseLocalDate(dateInput: string | Date | number): Date {
  if (dateInput instanceof Date) {
    if (isNaN(dateInput.getTime())) return new Date();
    return dateInput;
  }
  if (typeof dateInput === 'number') return new Date(dateInput);
  if (typeof dateInput === 'string' && dateInput.trim()) {
    const trimmed = dateInput.trim();
    // YYYY-MM-DD format
    const ymdMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymdMatch) {
      const year = parseInt(ymdMatch[1], 10);
      const month = parseInt(ymdMatch[2], 10) - 1;
      const day = parseInt(ymdMatch[3], 10);
      return new Date(year, month, day, 12, 0, 0); // Noon local time to avoid timezone boundary drift
    }
    const d = new Date(trimmed);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
}

/**
 * Formats any date into Gregorian calendar presentation according to locale (fa, sv, en).
 */
export function formatGregorianDate(
  dateInput: string | Date | number,
  lang: 'fa' | 'sv' | 'en' | string = 'fa',
  formatType: 'short' | 'medium' | 'long' | 'full' = 'medium'
): string {
  const d = parseLocalDate(dateInput);
  const locale = lang === 'sv' ? 'sv-SE' : lang === 'en' ? 'en-US' : 'fa-IR';

  const optionsMap: Record<string, Intl.DateTimeFormatOptions> = {
    short: { year: 'numeric', month: '2-digit', day: '2-digit', calendar: 'gregory' },
    medium: { year: 'numeric', month: 'short', day: 'numeric', calendar: 'gregory' },
    long: { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric', calendar: 'gregory' },
    full: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', calendar: 'gregory' }
  };

  const opts = optionsMap[formatType] || optionsMap.medium;
  try {
    return new Intl.DateTimeFormat(locale, opts).format(d);
  } catch (e) {
    // Fallback if browser environment lacks locale support
    return d.toISOString().split('T')[0];
  }
}

/**
 * Formats date and time into Gregorian calendar presentation.
 */
export function formatGregorianDateTime(
  dateInput: string | Date | number,
  lang: 'fa' | 'sv' | 'en' | string = 'fa'
): string {
  const d = parseLocalDate(dateInput);
  const dateStr = formatGregorianDate(d, lang, 'medium');
  const locale = lang === 'sv' ? 'sv-SE' : lang === 'en' ? 'en-US' : 'fa-IR';
  try {
    const timeStr = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    return `${dateStr} - ${timeStr}`;
  } catch (e) {
    return dateStr;
  }
}

/**
 * Formats a Gregorian date range (startDate to endDate).
 */
export function formatGregorianDateRange(
  startDateInput: string | Date | number,
  endDateInput: string | Date | number,
  lang: 'fa' | 'sv' | 'en' | string = 'fa'
): string {
  const startStr = formatGregorianDate(startDateInput, lang, 'medium');
  const endStr = formatGregorianDate(endDateInput, lang, 'medium');
  return `${startStr} – ${endStr}`;
}

/**
 * Formats time in HH:mm format according to locale digits.
 */
export function formatGregorianTime(
  dateInput: string | Date | number,
  lang: 'fa' | 'sv' | 'en' | string = 'fa'
): string {
  const d = parseLocalDate(dateInput);
  const locale = lang === 'sv' ? 'sv-SE' : lang === 'en' ? 'en-US' : 'fa-IR';
  try {
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  } catch (e) {
    return '00:00';
  }
}

/**
 * Returns today's date in local YYYY-MM-DD format based on the browser's/system's timezone.
 */
export function getLocalDateString(d: Date = new Date()): string {
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - (offset * 60 * 1000));
  return local.toISOString().split('T')[0];
}


